import { useGetContext } from '@api/lendingAPI/lapiHooks';
import {
    fetchPostIdCheck,
    fetchPostOnfidoStart
} from '@iwoca/lapi-client/edge';
import { getQueryParameter, getWindowLocation } from '@iwRouter/LocationHelper';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import _ from 'lodash';

import * as IwDataDogLogger from '../../../../../iwLogger/IwDataDogLogger';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import { TDeserialisedPerson } from '../../../../modals/idCheckVerificationModal/IdCheckVerificationModal.types';
import { hasIDVerseSplitTest } from '../../../../modals/idCheckVerificationModal/IdCheckVerificationModalHelper';

type TSendVerificationSMSParams = {
    person: TDeserialisedPerson;
    phoneNumber: string;
};

type TSendVerificationSMSResult = {
    sendVerificationSMS: (
        params: TSendVerificationSMSParams
    ) => Promise<Response>;
    isLoading: boolean;
    isError: boolean;
    error: Error | null;
};

type TSMSVerificationRequestBody = {
    data: {
        person_uid: string;
        phone_uid: string;
        redirect_url?: string;
        offer_id?: string;
    };
};

/**
 * Sends the ID-check verification SMS for the funding-requirements flow.
 * Mirrors the credit-card hook but reads offerId from the URL and refreshes
 * the funding-requirements queries instead of credit-card onboarding.
 */
export const useSendVerificationSMS = (): TSendVerificationSMSResult => {
    const stateKey = useStateKey();
    const queryClient = useQueryClient();
    const { data: site_context } = useGetContext();

    const isIDVerseSplitTest = hasIDVerseSplitTest(site_context);

    const {
        mutateAsync: sendVerificationSMS,
        isLoading,
        isError,
        error
    } = useMutation({
        mutationFn: async ({
            person,
            phoneNumber
        }: TSendVerificationSMSParams) => {
            const phoneEntry = _.find(person.phones, {
                value: phoneNumber
            });

            if (!phoneEntry?.uid) {
                throw new Error('Phone entry not found');
            }

            const requestBody: TSMSVerificationRequestBody = {
                data: {
                    person_uid: person.uid,
                    phone_uid: phoneEntry.uid
                }
            };

            let response = null;
            if (isIDVerseSplitTest) {
                const location = getWindowLocation();
                requestBody.data.redirect_url = location.href;
                requestBody.data.offer_id = getQueryParameter(
                    location,
                    'offerId'
                );

                response = await fetchPostIdCheck({
                    stateKey,
                    //@ts-expect-error:ignore
                    body: requestBody
                });
            } else {
                response = await fetchPostOnfidoStart({
                    stateKey,
                    body: requestBody
                });
            }

            // 400 signals the customer has exhausted their SMS attempts.
            if (response.status === 400) {
                throw new Error('MAXIMUM_ATTEMPTS_REACHED');
            }

            if (!response.ok) {
                throw new Error('Failed to send verification SMS');
            }

            return response;
        },
        onSuccess: async () => {
            // Refresh the funding-requirements list so the ID check flips to PENDING.
            await queryClient.invalidateQueries({
                queryKey: ['fetchGetFundingRequirementsCreditFacility']
            });
            await queryClient.invalidateQueries({
                queryKey: ['customer_state', stateKey]
            });
        },
        onError: (error: Error) => {
            IwDataDogLogger.logError('Failed to send verification SMS', {
                tags: {
                    reporter: 'useSendVerificationSMS',
                    error_message: error.message
                }
            });
        }
    });

    return {
        sendVerificationSMS,
        isLoading,
        isError,
        error: error as Error | null
    };
};
